/**
 * Crashlytics context — so a native crash report says WHICH app, WHICH venue, WHO.
 *
 * Crashlytics itself needs no JS: it starts with FirebaseApp.configure() in AppDelegate
 * (only for the app its plist belongs to) and catches native crashes on its own. This only
 * adds our context. Added 2026-09-29, same as the Planner / Shifts boot/crashlytics.ts.
 *
 * NATIVE ONLY — a no-op on the web.
 *
 * ⚠️ WHITE-LABELS REPORT INTO THE PROD-CLIENT FIREBASE APP. The plugin itself runs
 * FirebaseApp.configure() on load when nothing configured Firebase yet, and the only iOS
 * plist is prod-client's — so every white-label's crashes land there (AppDelegate's
 * "prod only" guard cannot stop the plugin). The `bundle_id` key below is how to tell them
 * apart in the console. The real fix is a Firebase iOS app + plist per white-label.
 * ⚠️ NEVER break the app: every call is caught and swallowed.
 * ⚠️ No personal data: Parse ids only, never a name, phone or email.
 */
import { boot } from 'quasar/wrappers'
import { Capacitor } from '@capacitor/core'
import Parse from 'parse'

export default boot(async ({ router }) => {
  if (!Capacitor.isNativePlatform()) return
  let C
  try {
    C = (await import('@capacitor-firebase/crashlytics')).FirebaseCrashlytics
  } catch (e) { return }

  const safe = (p) => { if (p && p.catch) p.catch(() => { /* never break the app */ }) }
  const key = (k, v) => safe(C.setCustomKey({ key: k, value: String(v || ''), type: 'string' }))

  key('app', 'client')
  try {
    const { App } = await import('@capacitor/app')
    const info = await App.getInfo()
    key('bundle_id', info.id)
    key('app_version', `${info.version} (${info.build})`)
  } catch (e) { /* never break the app */ }

  // The venue arrives in the route (params or query), under either of its historical names.
  let lastBiz = '', lastUser = ''
  router.afterEach((to) => {
    try {
      const biz = String(to.params.businessId || to.params.restaurantId
        || to.query.businessId || to.query.restaurantId || lastBiz || '')
      if (biz !== lastBiz) { lastBiz = biz; key('business_id', biz) }
      const u = Parse.User.current()
      const user = (u && u.id) || ''
      if (user !== lastUser) { lastUser = user; safe(C.setUserId({ userId: user })) }
    } catch (e) { /* never break the app */ }
  })
})
